import { SnakeCasedPropertiesDeep } from 'type-fest';
import { BillingContact } from '@square/web-payments-sdk-types';
import { GetPlaceDetailsResponse } from 'rpc/model/squareup/address/service';
import { billingAddressFromOrder } from 'typescript/utilities/order_data';
import {
  OrderAddress,
  OrderSummaryContactInformation,
} from 'typescript/types/api/solidshop';
import {
  ADDRESS_FIELDS,
  BILLING_PREFIX,
} from 'typescript/schemas/address_forms/address_fields';
import t from 'typescript/utilities/translate';
import { ZodSchema } from 'zod';

/**
 * Places API returns GB state data in the administrative_district_level_2 field
 * @param addressDetails SnakeCasedPropertiesDeep<GetPlaceDetailsResponse>
 * @returns address object with the administrative_district_level1 key fixed for GB
 */
export const fixGBStateDetails = (
  address: SnakeCasedPropertiesDeep<GetPlaceDetailsResponse>['address'],
) => {
  const fixedAddress = { ...address };

  fixedAddress.administrative_district_level1 =
    fixedAddress.administrative_district_level2;

  return fixedAddress;
};

/**
 * Build billing address form values
 * @param sameBillingAsShipping - boolean
 * @param billingAddressFormValues - Record<string, string | boolean>
 * @param shippingAddress - OrderAddress
 * @param contactInformation - OrderSummaryContactInformation
 * @returns billing address object
 */
export const buildBillingAddress = ({
  sameBillingAsShipping,
  billingAddressFormValues,
  shippingAddress,
  contactInformation,
}: {
  sameBillingAsShipping: boolean;
  billingAddressFormValues?: Record<string, string | boolean>;
  shippingAddress?: OrderAddress;
  contactInformation?: OrderSummaryContactInformation;
}) => {
  if (sameBillingAsShipping) {
    return billingAddressFromOrder(shippingAddress, contactInformation);
  }

  return billingAddressFormValues;
};

/**
 * Build billing address object from billing contact
 * @param billingContact - BillingContact
 * @returns billing address object
 */
export const buildBillingAddressFromBillingContact = ({
  billingContact,
}: {
  billingContact?: BillingContact;
}) => {
  if (!billingContact) {
    return {};
  }

  return {
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.first_name}`]: billingContact.givenName,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.last_name}`]: billingContact.familyName,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.phone}`]: billingContact.phone,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.address_line_1}`]:
      billingContact.addressLines[0] || '',
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.address_line_2}`]:
      billingContact.addressLines[1] || '',
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.locality}`]: billingContact.city,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.postal_code}`]:
      billingContact.postalCode,
    [`${BILLING_PREFIX}${ADDRESS_FIELDS.administrative_district_level_1}`]:
      billingContact.state,
  };
};

/* Admnistrative district level 1 value transformers
 * for - administrative_district_level_1 select inputs in the address form
 * the user autofill value may not always be the code, so we need to find the code
 * there are different transformers for different countries due to the way we set up
 * the mappings in mappings.ts
 */

interface AdministrativeDistrictCodeTransformerProps {
  userValue: string;
  options?: Record<string, string>;
}

/**
 * state/county code transformer that works for US/IE/AU
 * @param userValue - string
 * @param options - Record<string, string> - options for the select input
 * @returns string | undefined - representing the state/province code
 */
export const defaultStateCodeTransformer = ({
  userValue,
  options,
}: AdministrativeDistrictCodeTransformerProps): string | undefined => {
  // if the userValue is already a code, return it
  // uppercase the userValue since all the codes are uppercase
  if (options[userValue.toUpperCase()]) {
    return userValue;
  }

  // attempt to find the code for the userValue
  // we'll lowercase both values to make the comparison case insensitive
  const code = Object.keys(options).find(
    (code) => options[code].toLowerCase() === userValue.toLowerCase(),
  );

  return code;
};

/**
 * province code transformer for CA
 * this transformer is different than the default transformer because the user value
 * could be either in english or french and our own mappings are a translation key
 * @param userValue - string
 * @returns string | undefined - representing the state/province code
 */
export const canadaProvinceCodeTransformer = ({
  userValue,
  options,
}: AdministrativeDistrictCodeTransformerProps): string | undefined => {
  // if the userValue is already a code, return it
  // uppercase the userValue since all the codes are uppercase
  if (options[userValue.toUpperCase()]) {
    return userValue;
  }

  // we translate the mapping value to a real string to compare to the user value
  // we'll also lowercase both values to make the comparison case insensitive
  const code = Object.keys(options).find(
    (code) => t(options[code]).toLowerCase() === userValue.toLowerCase(),
  );

  return code;
};

/**
 * prefecture code transformer for JP
 * @param userValue - string
 * @returns string | undefined - representing the prefecture value
 */
export const japanPrefectureCodeTransformer = ({
  userValue,
  options,
}: AdministrativeDistrictCodeTransformerProps): string | undefined => {
  // if the userValue is already a code, return it
  // 99.999999% chance this never happens since the userValue should always be in Japanese
  if (options[userValue]) {
    return userValue;
  }

  // attempt to find the code for the userValue
  const code = Object.keys(options).find((code) => options[code] === userValue);

  return code;
};

export const administrativeDistrictCodeTransformers: {
  [key: string]: ({
    userValue,
    options,
  }: AdministrativeDistrictCodeTransformerProps) => string;
} = {
  US: defaultStateCodeTransformer,
  IE: defaultStateCodeTransformer,
  AU: defaultStateCodeTransformer,
  CA: canadaProvinceCodeTransformer,
  JP: japanPrefectureCodeTransformer,
};

export const isCompleteOrderAddress = (address?: OrderAddress) => {
  const requiredFields = [
    address?.first_name,
    address?.last_name,
    address?.address_line_1,
    address?.postal_code,
    address?.locality,
    address?.administrative_district_level_1,
  ];

  return requiredFields.every(Boolean);
};

export const removePrefixFromAddress = (
  address: Record<string, any>,
  prefix: string,
) => {
  const newAddress = { ...address };
  Object.keys(newAddress).forEach((key) => {
    if (key.startsWith(prefix)) {
      newAddress[key.replace(prefix, '')] = newAddress[key];
      delete newAddress[key];
    }
  });

  return newAddress;
};

/**
 * Check if the address is valid according to the schema
 * @param address - Record<string, any>
 * @param schema - ZodSchema
 * @returns boolean
 */
export const areFormFieldsValid = (
  address: Record<string, any>,
  schema: ZodSchema,
) => {
  try {
    schema.parse(address);
    return true;
  } catch {
    return false;
  }
};
